// src/App.js — Root v10 — TradeLabPage + AIAssistant + ErrorBoundary + PWA
import { useState, useEffect } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { G } from './constants';
import { updateSocketToken } from './hooks/useSocket';
import { auth } from './services/api';

import ErrorBoundary from './components/ErrorBoundary';
import Topbar        from './components/Topbar';
import Sidebar       from './components/Sidebar';
import BottomNav     from './components/BottomNav';
import Toaster             from './components/Toaster';
import GamificationOverlay from './components/GamificationOverlay';
import Logo          from './components/Logo';
import AIAssistant   from './components/AIAssistant';
import { SkeletonStyles } from './components/Skeleton';
import OfflineBanner from './components/OfflineBanner';
import SuspendedScreen from './components/SuspendedScreen';

import AuthScreen      from './pages/AuthScreen';
// ── Lazy loading: split each page into its own JS chunk ──────────
// This reduces initial bundle size by ~75% — pages load on demand
import { lazy, Suspense } from 'react';

// ── ChunkLoadError auto-recovery (S-107, 2026-06-25) ─────────────────────────
// After a deploy, an OLD index.html (cached, or an already-open tab) references
// JS chunk hashes that no longer exist. Cloudflare's SPA fallback then returns
// index.html (text/html) for the missing chunk -> "Loading chunk N failed" / MIME
// error -> the app crashes to the ErrorBoundary. lazyRetry catches that failed
// import and hard-reloads ONCE to fetch the fresh index.html + correct chunks.
// A 10s sessionStorage guard prevents reload loops if a chunk is genuinely gone.
function lazyRetry(factory) {
  return lazy(() =>
    factory().catch((err) => {
      const KEY = 'pc_chunk_reload_ts';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return new Promise(() => {}); // halt render - the page is reloading
      }
      throw err; // already reloaded recently -> surface the real error
    })
  );
}
// v270 (G4.11.1): OnboardingModal deleted — replaced by dedicated /welcome page route for AdSense compliance.
// S-042 (2026-05-10) Fix #4: SessionTimeoutWarning removed per Sandeep's mandate.
// No replacement — auto-refresh logic in api.js still handles silent token refresh
// when session expires; no need for warning banner.
const HomePage        = lazyRetry(() => import('./pages/HomePage'));
const TradePage       = lazyRetry(() => import('./pages/TradePage'));
const NewsPage        = lazyRetry(() => import('./pages/NewsPage'));
const ForumsPage      = lazyRetry(() => import('./pages/ForumsPage'));
const ClubDetailPage  = lazyRetry(() => import('./pages/ClubDetailPage')); // v230 Session A
const ProfilePage     = lazyRetry(() => import('./pages/ProfilePage'));
const InboxPage       = lazyRetry(() => import('./pages/InboxPage')); // v199c2b
const AlertsPage      = lazyRetry(() => import('./pages/AlertsPage'));
const OrdersPage      = lazyRetry(() => import('./pages/OrdersPage'));
const PositionsPage   = lazyRetry(() => import('./pages/PositionsPage'));
const WatchlistPage   = lazyRetry(() => import('./pages/WatchlistPage'));
const PnLPage         = lazyRetry(() => import('./pages/PnLPage'));
const PremiumPage     = lazyRetry(() => import('./pages/PremiumPage'));
const SettingsPage    = lazyRetry(() => import('./pages/SettingsPage'));
const SupportPage     = lazyRetry(() => import('./pages/SupportPage'));
const SharePage       = lazyRetry(() => import('./pages/SharePage'));
const SavedPage       = lazyRetry(() => import('./pages/SavedPage'));
const LegalPage       = lazyRetry(() => import('./pages/LegalPage'));
const NotifsPage      = lazyRetry(() => import('./pages/NotifsPage'));
const AboutPage       = lazyRetry(() => import('./pages/AboutPage'));
const CopyTradingPage = lazyRetry(() => import('./pages/CopyTradingPage'));
const TradeLabPage    = lazyRetry(() => import('./pages/TradeLabPage'));
const ChartPage       = lazyRetry(() => import('./pages/ChartPage'));
const AdminDashboard  = lazyRetry(() => import('./pages/AdminDashboard'));
const TopMoversPage   = lazyRetry(() => import('./pages/TopMoversPage'));
const LeaderboardPage = lazyRetry(() => import('./pages/LeaderboardPage'));
const SignalEnginePage = lazyRetry(() => import('./pages/SignalEnginePage'));
const GamificationPage = lazyRetry(() => import('./pages/GamificationPage'));
const StorePage        = lazyRetry(() => import('./pages/StorePage'));
const AuctionPage      = lazyRetry(() => import('./pages/AuctionPage'));
// v235: TournamentPage removed (B+ plan — duplicates GamificationPage challenges)
// v187: Advanced orders page (limit IOC/FOK/Post-Only + OCO)
const AdvancedOrderPage = lazyRetry(() => import('./pages/AdvancedOrderPage'));
// v270 (G4.11.1): Dedicated welcome route — replaces OnboardingModal full-screen overlay (AdSense compliance)
const WelcomePage       = lazyRetry(() => import('./pages/WelcomePage'));

function InnerApp() {
  const {
    user, setUser, authDone, page,
    showAd, setShowAd, adTimer, ADS_ENABLED,
    showSub, setShowSub,
    // S-084: Context-aware modal content selector
    subModalContext, setSubModalContext,
    toasts, watchAd, cancelAd, setPage,
    bg, bg2, bord, text, tx2, s,
    refetchPort, refetchUserStats,
  } = useApp();

  // ── PWA Install Prompt ─────────────────────────────────
  const [installPrompt,    setInstallPrompt]    = useState(null);
  const [showInstall,      setShowInstall]      = useState(false);
  const [installDismissed, setInstallDismissed] = useState(() => {
    try { return localStorage.getItem('pc_install_dismissed') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!installDismissed) setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [installDismissed]);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') { setShowInstall(false); setInstallPrompt(null); }
  };

  const dismissInstall = () => {
    setShowInstall(false);
    try { localStorage.setItem('pc_install_dismissed', 'true'); } catch {}
    setInstallDismissed(true);
  };

  if (!authDone) {
    return (
      <div style={{ minHeight:'100vh', background:'#080600', display:'flex', flexDirection:'column',
          alignItems:'center', justifyContent:'center', gap:16 }}>
        <Logo size={72}/>
        <div style={{ color:G, fontSize:11, letterSpacing:3, fontFamily:"'Cinzel',Georgia,serif", opacity:0.7 }}>
          LOADING...
        </div>
      </div>
    );
  }

  if (!user) return <AuthScreen onLogin={u => {
    // v270 (G4.11.1): First-time users land on dedicated /welcome route (AdSense compliant)
    // instead of an auto-triggered modal overlay. Returning users (pc_onboarded set) skip straight to home.
    let landingPage = 'home';
    try {
      if (!localStorage.getItem('pc_onboarded')) landingPage = 'welcome';

    // Handle password reset URL (/reset-password?token=xxx)
    try {
      const params = new URLSearchParams(window.location.search);
      const resetToken = params.get('token');
      const isResetPath = window.location.pathname.includes('reset-password');
      if (isResetPath && resetToken) {
        window._pcResetToken = resetToken;
        // Clean URL without reload
        window.history.replaceState({}, '', '/');
      }
    } catch(_) {}
    } catch {}
    try { sessionStorage.setItem('pc_page', landingPage); } catch {}
    setPage(landingPage);
    setUser(u);
    // FIX: update socket token so reconnects after login carry the JWT
    try { const tok = localStorage.getItem('pc_token'); if (tok) updateSocketToken(tok); } catch {}
    // Immediately refresh portfolio + userStats after login
    try { refetchPort(); } catch {}
    try { refetchUserStats(); } catch {}
    // S-036 (2026-05-08): Defensive /me refresh after login (Option B safety net).
    // Login response already includes role + admin_level (S-036 Fix #1), so this is
    // belt-and-suspenders: if any future field is added to /me but forgotten in /login,
    // frontend still gets the full picture. Fire-and-forget — login already succeeded
    // by the time we reach this line. ~50-150ms extra round-trip is acceptable for
    // the safety guarantee. React's setState batching makes the second setUser cheap.
    auth.me().then(res => {
      if (res?.success && res?.user) setUser(res.user);
    }).catch(() => { /* /me fetch failed — login still succeeded, user state already set */ });
  }} />;

  const renderPage = () => {
    switch (page) {
      case 'welcome':       return <WelcomePage/>;  // v270 (G4.11.1): Dedicated first-time-user route
      case 'home':          return <HomePage/>;
      case 'trade':         return <TradePage/>;
      case 'news':          return <NewsPage/>;
      case 'forums':        return <ForumsPage/>;
      case 'clubDetail':    return <ClubDetailPage/>;
      case 'inbox':         return <InboxPage/>;
      case 'profile':       return <ProfilePage/>;
      case 'alerts':        return <AlertsPage/>;
      case 'orders':        return <OrdersPage/>;
      case 'positions':     return <PositionsPage/>;
      case 'watchlist':     return <WatchlistPage/>;
      case 'pnl':           return <PnLPage/>;
      case 'premium':       return <PremiumPage/>;
      case 'settings':      return <SettingsPage/>;
      case 'support':       return <SupportPage/>;
      case 'share':         return <SharePage/>;
      case 'saved':         return <SavedPage/>;
      case 'notifications': return <NotifsPage/>;
      case 'about':         return <AboutPage/>;
      case 'admin':         return <AdminDashboard onBack={() => setPage('home')}/>;
      case 'legal':         return <LegalPage/>;
      case 'privacy':       return <LegalPage initialTab='privacy'/>;
      case 'terms':         return <LegalPage initialTab='terms'/>;
      case 'refund':        return <LegalPage initialTab='refund'/>;
      case 'copytrading':   return <CopyTradingPage/>;
      case 'tradelab':      return <TradeLabPage/>;
      case 'gamification':  return <GamificationPage/>;
      case 'signals':        return <SignalEnginePage/>;
      case 'chart':         return <ChartPage/>;
      case 'movers':        return <TopMoversPage/>;
      case 'leaderboard':   return <LeaderboardPage/>;
      case 'store':         return <StorePage/>;
      case 'auction':       return <AuctionPage/>;
      // v235: tournament route removed (B+ plan)
      case 'advanced-orders': return <AdvancedOrderPage/>;
      default:              return <HomePage/>;
    }
  };

  return (
    <Suspense fallback={<div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#080600', color:'#F5C518' }}>Loading...</div>}>
    <div style={{ minHeight:'100vh', background:bg, color:text, fontFamily:"'Inter','Segoe UI','Roboto',sans-serif" }}>
      <style>{`
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:3px;height:3px}
        ::-webkit-scrollbar-thumb{background:${G}40;border-radius:4px}
        body{margin:0;padding:0;overflow-x:hidden;font-family:'Inter','Segoe UI','Roboto',system-ui,sans-serif}
        input,select,textarea{box-sizing:border-box;font-family:'Inter','Segoe UI','Roboto',system-ui,sans-serif}
        @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes slideInUp{from{opacity:0;transform:translateY(100%)}to{opacity:1;transform:translateY(0)}}
        .page{animation:slideUp 0.25s ease}

        /* v136: Button physics — scale + bounce */
        button,[role="button"]{transition:transform 0.12s cubic-bezier(0.25,0.46,0.45,0.94),box-shadow 0.15s ease;position:relative;overflow:hidden}
        button:active,[role="button"]:active{transform:scale(0.95)!important}

        /* v136: Reward satisfaction animations */
        @keyframes coinFly{0%{opacity:1;transform:translateY(0) scale(1)}60%{opacity:1;transform:translateY(-60px) scale(1.2)}100%{opacity:0;transform:translateY(-120px) scale(0.5)}}
        @keyframes floatUp{0%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(-50px)}}
        @keyframes glowPulse{0%,100%{box-shadow:0 0 4px rgba(245,197,24,0.2)}50%{box-shadow:0 0 16px rgba(245,197,24,0.6)}}
        @keyframes winGlow{0%{box-shadow:0 0 0 rgba(14,203,129,0)}50%{box-shadow:0 0 20px rgba(14,203,129,0.4)}100%{box-shadow:0 0 0 rgba(14,203,129,0)}}
        @keyframes lossShake{0%,100%{transform:translateX(0)}20%{transform:translateX(-4px)}40%{transform:translateX(4px)}60%{transform:translateX(-3px)}80%{transform:translateX(3px)}}
        @keyframes levelUp{0%{transform:scale(0.5);opacity:0}50%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
        .coin-fly{animation:coinFly 1.2s ease forwards;pointer-events:none;position:absolute;z-index:9999}
        .float-reward{animation:floatUp 1.5s ease forwards;pointer-events:none;position:absolute;z-index:9999;font-weight:800;font-size:14px;color:#F5C518;text-shadow:0 1px 4px rgba(0,0,0,0.5)}
        .glow-wallet{animation:glowPulse 1s ease 2}
        .win-card{animation:winGlow 0.8s ease}
        .loss-card{animation:lossShake 0.4s ease}
        .level-up-badge{animation:levelUp 0.6s cubic-bezier(0.34,1.56,0.64,1)}
      `}</style>
      <SkeletonStyles/>

      <Topbar/>
      <Sidebar/>
      <ErrorBoundary>
        <div className="page" style={{ paddingBottom:70 }}>{renderPage()}</div>
      </ErrorBoundary>
      <BottomNav/>
      <Toaster toasts={toasts}/>
        <GamificationOverlay userId={user?.id}/>

      {/* ── AI Assistant — floating button, always rendered when logged in ── */}
      <AIAssistant/>

      {/* ── v270 (G4.11.1): Always-mounted system components.
          PREVIOUSLY these were nested INSIDE the watch-ad modal gate (a pre-existing structural bug),
          meaning they only rendered when the user was watching an ad. Now correctly top-level. ── */}
      {user?.is_banned && <SuspendedScreen/>}
      {/* S-042 (2026-05-10) Fix #4: SessionTimeoutWarning removed per Sandeep mandate */}
      <OfflineBanner/>

      {/* ── Ad Modal — disabled when ADS_ENABLED=false ── */}
      {ADS_ENABLED && showAd && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.95)', zIndex:9000,
          display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:bg2, border:`2px solid ${G}`, borderRadius:16, padding:24,
            maxWidth:380, width:'94%', textAlign:'center' }}>
            <div style={{ fontWeight:800, fontSize:17, color:G, marginBottom:8 }}>
              📺 Watch Ad → Earn 1 Free Trade
            </div>
            <div style={{ color:tx2, fontSize:12, marginBottom:14 }}>
              Watch the full ad to earn a free trade ({adTimer > 0 ? `${adTimer}s remaining` : '✓ Done!'})
            </div>

            {/* v270 (G4.11.1): Removed OnboardingModal usage here — now a dedicated /welcome page route.
                AdSense compliance: 0.92 opacity overlay covering AdBanner was a Better Ad Standards violation. */}

            {/* Google AdSense Ad Unit */}
            <div style={{ background:'#0D0D0D', borderRadius:10, marginBottom:14,
              border:`1px solid ${bord}`, minHeight:250, overflow:'hidden',
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
              {/* Real AdSense unit — replace data-ad-client and data-ad-slot with your values */}
              {/* AdSense unit — initialized via effect when ad modal opens */}
              <ins className="adsbygoogle"
                style={{ display:'block', width:'100%', minHeight:250 }}
                data-ad-client={process.env.REACT_APP_ADSENSE_CLIENT || 'ca-pub-XXXXXXXXXXXXXXXX'}
                data-ad-slot={process.env.REACT_APP_ADSENSE_SLOT || '1234567890'}
                data-ad-format="rectangle"
                data-full-width-responsive="true"
                data-adsbygoogle-initialized="false"/>
              {/* Fallback display while ad loads */}
              <div style={{ color:tx2, fontSize:12, padding:20, textAlign:'center' }}>
                <div style={{ fontSize:36, marginBottom:8 }}>📺</div>
                <div style={{ fontWeight:700, color:G, fontSize:20 }}>{adTimer > 0 ? adTimer : '✓'}</div>
                <div style={{ fontSize:11, marginTop:4 }}>
                  {adTimer > 0 ? `Ad playing — ${adTimer}s left` : 'Ad complete!'}
                </div>
              </div>
            </div>

            {/* Countdown bar */}
            <div style={{ background:'#333', borderRadius:20, height:6, marginBottom:16, overflow:'hidden' }}>
              <div style={{ background:`linear-gradient(90deg,${G},#0ECB81)`, height:'100%', borderRadius:20,
                width: `${((30 - adTimer) / 30) * 100}%`, transition:'width 1s linear' }}/>
            </div>

            {adTimer <= 0 ? (
              <button style={{ ...s.btn, width:'100%', padding:'14px', fontSize:15 }}
                onClick={() => setShowAd(false)}>
                🎁 Claim Your Free Trade!
              </button>
            ) : (
              // ── v275 (G4.15-WEBAPP-REWARDED-AD-AUDIT): AdSense Rewarded-Ad compliance ──
              // Required by https://support.google.com/adsense/answer/9121589 implementation
              // rule #2: "Rewarded Ads must not oblige users to interact with it... it must
              // be possible to skip or dismiss them." User can now exit mid-countdown via
              // Cancel button — reward is NOT granted (Reward Rule #6: delivered only on
              // completion of the required action). The previous non-skippable wording was
              // removed because that language is exactly what AdSense reviewers flag.
              <div>
                <div style={{ color:tx2, fontSize:12, marginBottom:10, textAlign:'center' }}>
                  Ad in progress... reward delivered on completion ({adTimer}s)
                </div>
                <button
                  onClick={cancelAd}
                  style={{ ...s.btnO, width:'100%', padding:'10px', fontSize:12, opacity:0.85 }}>
                  ✕ Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Trade Limit Modal (S-084: Now Context-Aware) ── */}
      {/* S-084 (2026-05-28): Modal content now varies by subModalContext set by trigger.
          PREVIOUS: One-size-fits-all "Daily Limit Reached" message — misleading when
          paid users clicked the leverage upgrade nudge (got "3 free trades used" text).
          NEW: Three contexts — daily_limit (free user limit), leverage (tier upgrade),
          ads_disabled (ads off fallback). Falls through to daily_limit if unset (safe default).
          Closing the modal also resets subModalContext to null so next open is clean. */}
      {showSub && (() => {
        // Determine modal content per context
        const ctx = subModalContext || 'daily_limit';
        let icon, title, message, primaryLabel, primaryAction;
        if (ctx === 'leverage') {
          // Paid (or free) user wants higher leverage — route to Premium with Elite pre-selected
          icon = '🚀';
          title = 'Unlock Higher Leverage';
          message = 'Higher leverage tiers are available on Pro (50x), Pro (100x), and Pro (125x). Upgrade your plan to trade with more leverage.';
          primaryLabel = '⭐ View Plans';
          primaryAction = () => {
            try { sessionStorage.setItem('pc_premium_preselect', '12m'); } catch (_) {}
            setShowSub(false); setSubModalContext?.(null); setPage('premium');
          };
        } else if (ctx === 'ads_disabled') {
          icon = '📺';
          title = 'Ads Unavailable';
          message = 'Rewarded ads are temporarily unavailable. Upgrade to Pro for unlimited trading — no ads required.';
          primaryLabel = '⭐ Go Pro';
          primaryAction = () => { setShowSub(false); setSubModalContext?.(null); setPage('premium'); };
        } else {
          // Default — daily_limit (original behavior preserved for all existing call sites)
          icon = '⭐';
          title = 'Derivatives Limit Reached';
          // ECONOMY: free SPOT trading is UNLIMITED. The only remaining daily limit is on
          // derivatives (futures/options), which on web is 3/day with no ad option (Rule O).
          // The old copy said "3 free daily trades" for everything, which is now simply false
          // and makes users self-limit on spot when nothing is stopping them.
          message = "You've used your free futures and options trades for today. Spot trading stays unlimited — upgrade to Pro for unlimited derivatives.";
          primaryLabel = '⭐ Go Pro';
          primaryAction = () => { setShowSub(false); setSubModalContext?.(null); setPage('premium'); };
        }
        const closeModal = () => { setShowSub(false); setSubModalContext?.(null); };
        return (
        <div style={{ position:'fixed', inset:0,
          // v270 (G4.11.1): AdSense compliance — opacity 0.35 + blur(6px). Was 0.92 (Better Ad Standards violation).
          // Trade-limit hits free users daily — high-frequency surface, must be compliant.
          background:'rgba(0,0,0,0.35)', backdropFilter:'blur(6px)', WebkitBackdropFilter:'blur(6px)',
          zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:bg2, border:`2px solid ${G}`, borderRadius:16, padding:24,
            maxWidth:360, width:'92%', textAlign:'center' }}>
            <div style={{ fontSize:44, marginBottom:10 }}>{icon}</div>
            <div style={{ fontWeight:800, fontSize:17, color:G, marginBottom:8 }}>{title}</div>
            <div style={{ color:tx2, fontSize:13, marginBottom:20 }}>{message}</div>
            <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
              {/* Watch Ad button only relevant to daily_limit context with ads enabled */}
              {ADS_ENABLED && ctx === 'daily_limit' && (
                <button style={s.btn}  onClick={() => { setShowSub(false); setSubModalContext?.(null); watchAd(); }}>📺 Watch Ad</button>
              )}
              <button style={s.btn}  onClick={primaryAction}>{primaryLabel}</button>
              <button style={s.btnO} onClick={closeModal}>Later</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── PWA Install Banner ── */}
      {showInstall && installPrompt && (
        <div style={{ position:'fixed', bottom:62, left:0, right:0, zIndex:8000,
          padding:'0 12px', animation:'slideInUp 0.35s ease' }}>
          <div style={{ background:bg2, border:`1px solid ${G}`, borderRadius:14, padding:'12px 16px',
            display:'flex', alignItems:'center', gap:12,
            boxShadow:'0 -4px 24px rgba(0,0,0,0.5)' }}>
            <Logo size={38}/>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:800, fontSize:13, color:G }}>Install Paper Crypto</div>
              <div style={{ color:tx2, fontSize:11 }}>Add to home screen for the best experience</div>
            </div>
            <button style={{ ...s.btn, padding:'8px 14px', fontSize:12, flexShrink:0 }}
              onClick={handleInstall}>Install</button>
            <div onClick={dismissInstall}
              style={{ color:tx2, cursor:'pointer', fontSize:18, flexShrink:0, padding:'0 4px' }}>✕</div>
          </div>
        </div>
      )}
    </div>
  </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <InnerApp/>
      </AppProvider>
    </ErrorBoundary>
  );
}
